import { useState } from "react";
import { toast } from "react-toastify";
import { BASE_URL } from "../services/api";
import { GST_STATES } from "./gstStates";

const emptyForm = {
  userName: "",
  email: "",
  password: "",
  confirmPassword: "",
  phone: "",
  distributorName: "",
  address: "",
  gstin: "",
  distributorPhone: "",
  stateCode: "",
  city: "",
  pincode: "",
};

/**
 * Distributor sign-up: creates the distributor and its first user (super admin of that distributor) in one step. After it
 * succeeds the user logs in from the login page.
 */
const SignupPage = ({ onBack, onSignedUp }) => {
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const set = (field) => (e) => setForm((prev) => ({ ...prev, [field]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (form.password !== form.confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }
    setSaving(true);
    try {
      const { confirmPassword, ...payload } = form;
      const body = Object.fromEntries(Object.entries(payload).map(([key, value]) => [key, value.trim() === "" ? null : value.trim()]));
      body.password = form.password; // passwords are sent as typed
      const response = await fetch(`${BASE_URL}/api/v1/auth/signup`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      const text = await response.text();
      let result = {};
      try {
        result = text ? JSON.parse(text) : {};
      } catch {
        result = { message: text };
      }
      if (!response.ok) {
        const fieldError = result?.fieldErrors?.[0];
        toast.error(fieldError ? `${fieldError.field}: ${fieldError.message}` : result?.detail || result?.message || "Sign up failed");
        return;
      }
      toast.success(result.message || "Account created. Please log in.");
      onSignedUp?.(result.email || form.email);
    } catch (error) {
      console.error("Sign up error:", error);
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="login-container">
      <form className="login-form" style={{ width: "560px", maxWidth: "94vw" }} onSubmit={submit}>
        <h2>Create Distributor Account</h2>

        <h4 style={{ margin: "8px 0 6px" }}>Your login</h4>
        <div className="form-grid">
          <input placeholder="Your name *" value={form.userName} onChange={set("userName")} required maxLength={150} />
          <input type="email" placeholder="Email (used to log in) *" value={form.email} onChange={set("email")} required maxLength={150} />
          <input type="tel" placeholder="Mobile number *" value={form.phone} onChange={set("phone")} required maxLength={30} />
          <input type="password" placeholder="Password (min 8 characters) *" value={form.password} onChange={set("password")} required minLength={8} />
          <input type="password" placeholder="Confirm password *" value={form.confirmPassword} onChange={set("confirmPassword")} required minLength={8} />
        </div>

        <h4 style={{ margin: "14px 0 6px" }}>Distributor details</h4>
        <div className="form-grid">
          <input placeholder="Distributor / company name *" value={form.distributorName} onChange={set("distributorName")} required maxLength={200} />
          <input placeholder="GSTIN" value={form.gstin} onChange={set("gstin")} maxLength={20} />
          <input placeholder="Address *" value={form.address} onChange={set("address")} required maxLength={500} />
          <input placeholder="City" value={form.city} onChange={set("city")} maxLength={100} />
          <select value={form.stateCode} onChange={set("stateCode")}>
            <option value="">Select state</option>
            {GST_STATES.map((s) => (
              <option key={s.code} value={s.code}>
                {s.name} ({s.code})
              </option>
            ))}
          </select>
          <input placeholder="Pincode" value={form.pincode} onChange={set("pincode")} maxLength={10} />
          <input type="tel" placeholder="Company phone (defaults to your mobile)" value={form.distributorPhone} onChange={set("distributorPhone")} maxLength={30} />
        </div>

        <button type="submit" className="primary" disabled={saving} style={{ width: "100%", marginTop: "14px", ...(saving ? { opacity: 0.6 } : {}) }}>
          {saving ? "Creating account..." : "Sign up"}
        </button>
        <div style={{ marginTop: "12px", fontSize: "0.85rem", textAlign: "center" }}>
          <span onClick={saving ? undefined : onBack} style={{ cursor: "pointer", color: "#007bff" }}>
            Already have an account? Log in
          </span>
        </div>
      </form>
    </div>
  );
};

export default SignupPage;
